// scheduling/components/PlaceholderAssignModal.jsx
// Pick a staffer to convert a placeholder into real ScheduleEntries.
// Reuses querySuggestions (filtered by Level + DepartmentCode) so the candidate
// list is scoped to the placeholder's pool. Card styling mirrors StaffSuggestions
// (MatchBadge, score tier card, minibar fill, 5-week availability heatmap) so
// the two surfaces feel like the same product.
//
// Picking a candidate expands their card in place to show:
//   • Extend before / Extend after toggles
//   • Daily Hours grid (collapsed by default, tweak per-day)
//   • Before / After Summary (calendar + capacity view) showing how the
//     proposed placeholder distribution stacks on the staffer's existing schedule
//
// On Confirm: schedWrite assign_placeholder runs INSERT + DELETE in one tx with
// the explicit per-day entries (not the server-side default Mon-Fri spread).

import { useState, useEffect, useMemo } from 'react';
import { fetchSuggestions } from '../services/schedData';
import { assignPlaceholder, deletePlaceholder, upsertPlaceholder } from '../services/schedApi';
import { BRAND, STAFF_DEPT_GROUPS, PLACEHOLDER_LEVELS, isSchedulableStaff } from '../utils/schedConstants';
import { computeOptimizedHours } from '../utils/optimizeHours';
import { computeStaffWeeklyUtil } from '../utils/capacity';
import { useGenTrackBands } from '../hooks/useGenTrackBands';
import { jobBucketOf } from '../utils/genTrack';
import MatchBadge from './MatchBadge';
import NicheGapFlag from './NicheGapFlag';
import GenTrackFlag from './GenTrackFlag';
import OnSiteFilterNote from './OnSiteFilterNote';
import DailyHoursGrid from './distribution/DailyHoursGrid';
import ExtendToggles from './distribution/ExtendToggles';
import BeforeAfterSummary from './distribution/BeforeAfterSummary';
import { SENTINEL_JOB_ID, TOUR_SUGGESTION_CANDIDATES } from '../../components/tour/tourDemoData';

function toISO(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(typeof iso === 'string' ? iso.slice(0, 10) + 'T12:00:00' : iso)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Heatmap cell date label (M/D) — parse-safe slice, no Date needed.
function fmtMD(iso) { if (!iso) return ''; const [, m, d] = String(iso).slice(0, 10).split('-'); return `${parseInt(m, 10)}/${parseInt(d, 10)}`; }

// Mon-Fri inclusive between two ISO dates, plus Sat if the staffer has Saturday
// capacity on that date. Skips Sundays unconditionally.
function buildCandidateDates(startISO, endISO, staffId, getDayCap) {
  if (!startISO || !endISO) return [];
  const s = new Date(String(startISO).slice(0, 10) + 'T12:00:00');
  const e = new Date(String(endISO).slice(0, 10) + 'T12:00:00');
  const out = [];
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow === 0) continue; // skip Sunday
    const iso = toISO(new Date(d));
    if (dow === 6) {
      const cap = staffId != null && getDayCap ? getDayCap(staffId, iso) : 0;
      if (cap <= 0) continue;
    }
    out.push(iso);
  }
  return out;
}

export default function PlaceholderAssignModal({
  placeholder,
  currentUser,
  getDayCap,
  entries,
  dayHoursByStaff,
  ptoByStaffDay,
  holidayByDate,
  globalAssignmentByDate,
  onClose,
  onAssigned,
  onDeleted,
  // Every grid-eligible staffer. Only used for CUSTOM-job placeholders, where
  // querySuggestions has nothing to score against — see the candidate effect.
  staffList,
  // Called after an in-place edit so the grid can refetch placeholders.
  onEdited,
}) {
  const [candidates, setCandidates] = useState(null); // null=loading, []=empty
  const [error, setError] = useState(null);
  const [pickedStaffId, setPickedStaffId] = useState(null);
  const [availability, setAvailability] = useState({}); // { [staffId]: [{ weekStart, capacity, scheduled, pct }] }
  const [availMeta, setAvailMeta] = useState({ spanned: null }); // assignment week-span (for the heatmap bracket)
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nicheMissing, setNicheMissing] = useState(false); // non-individual client with no real niche
  // The job's service line, kept from the suggestions response purely so the Gen Track
  // badge can tell whether THIS job is on the side a candidate is short of. Only
  // serviceDesc is available here (querySuggestions never returns ServiceIndex), which
  // jobBucketOf handles via its ServiceDesc fallback.
  const [jobServiceDesc, setJobServiceDesc] = useState(null);
  // On-site info from the same suggestions response. This modal was the ONLY ranked
  // suggestion list in the app with no office handling at all -- it filtered on Level +
  // DepartmentCode and nothing else, so an on-site job in Birmingham would happily
  // propose Huntsville staff.
  const [jobOnSite, setJobOnSite] = useState({ isOnSite: false, office: null, officeCode: null });

  // Per-staff preview state — keyed by staffId so switching cards keeps prior tweaks.
  const [previewByStaff, setPreviewByStaff] = useState({});

  // A placeholder holds a real job OR a custom job (migrate-074). Custom ones have no
  // client, niche or service line, so everything job-derived here is unavailable.
  const isCustomJob = placeholder?.CustomJobId != null;

  // ── In-place edit ────────────────────────────────────────────────────────
  // Placeholders used to be delete-and-recreate: the only way to move one to a
  // different level was to throw it away. upsertPlaceholder already accepted a
  // placeholderId and updated every field — this was purely a missing UI.
  const [editing, setEditing] = useState(false);
  const [editDept, setEditDept] = useState('');
  const [editLevel, setEditLevel] = useState('');
  const [editHours, setEditHours] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editError, setEditError] = useState(null);
  // shape: { [staffId]: { proposedDailyHours: {iso:hrs}, candidateDates: ISO[], overflowStart: bool, overflowEnd: bool } }

  const totalHours = useMemo(() => Number(placeholder?.Hours || 0), [placeholder]);
  const dateLabel = useMemo(() => {
    if (!placeholder) return '';
    return `${fmtDate(placeholder.StartDate)} — ${fmtDate(placeholder.EndDate)}`;
  }, [placeholder]);

  // Gen Track balance chip. Shares one cached fetch with every other candidate surface.
  const { bandByStaff, lists: genTrackLists } = useGenTrackBands();
  const jobBucket = useMemo(
    () => jobBucketOf({ ServiceDesc: jobServiceDesc }, genTrackLists),
    [jobServiceDesc, genTrackLists],
  );

  // 1) Pull suggestions scoped to (jobId, level, departmentCode).
  useEffect(() => {
    if (!placeholder) return;
    // Guided-tour: the sentinel placeholder shows curated sample candidates (no API call).
    if (placeholder.JobId === SENTINEL_JOB_ID) {
      setCandidates(TOUR_SUGGESTION_CANDIDATES);
      setError(null);
      return;
    }
    // Custom job: there is nothing to SCORE against. Match tiers come from the
    // client's niche + the job's service line, and a custom job has neither, so
    // calling querySuggestions would either fail or return a meaningless ranking.
    // Build the pool locally instead — everyone at this (level, dept) who can hold
    // scheduled work — and let the availability heatmap below do the deciding.
    if (isCustomJob) {
      const pool = (staffList || [])
        .filter(s => s.Level === placeholder.Level
          && s.DepartmentCode === placeholder.DepartmentCode
          && isSchedulableStaff(s.Level))
        .map(s => ({ ...s, totalScore: null }))
        .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
      setCandidates(pool);
      setError(null);
      setNicheMissing(false);
      setJobServiceDesc(null);
      setJobOnSite({ isOnSite: false, office: null, officeCode: null });
      return;
    }
    let cancelled = false;
    setCandidates(null);
    setError(null);
    setNicheMissing(false);
    fetchSuggestions(placeholder.JobId, {
      level: placeholder.Level,
      departmentCode: placeholder.DepartmentCode,
    })
      .then(data => {
        if (cancelled) return;
        setNicheMissing(!!data?.job?.nicheMissing);
        setJobServiceDesc(data?.job?.serviceDesc || null);
        setJobOnSite({
          isOnSite: !!data?.job?.isOnSite,
          office: data?.job?.office || null,
          officeCode: data?.job?.officeCode || null,
        });
        // querySuggestions returns { candidates, job, weights, filteredCount }
        const rows = data?.candidates || (Array.isArray(data) ? data : []);
        // On-site jobs restrict to the job's office, matching every other ranked
        // suggestion list (baseSuggestionFilter does the same thing). Hard filter, no
        // toggle -- to reach another office, use a surface with a search box.
        const onSiteCode = (data?.job?.isOnSite && data?.job?.officeCode) ? data.job.officeCode : null;
        const filtered = rows.filter(r =>
          r.Level === placeholder.Level && r.DepartmentCode === placeholder.DepartmentCode
          && (!onSiteCode || r.OfficeCode === onSiteCode)
        );
        // Sort by score desc, ties broken by name
        filtered.sort((a, b) => {
          const sd = (b.totalScore || 0) - (a.totalScore || 0);
          if (sd !== 0) return sd;
          return (a.Name || '').localeCompare(b.Name || '');
        });
        setCandidates(filtered);
      })
      .catch(e => { if (!cancelled) { setError(e.message); setCandidates([]); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, isCustomJob, staffList]);

  // 2) Compute the heatmap with the shared capacity util (same logic as the grid)
  //    over the placeholder's StartDate→EndDate window (min 5, max 13 weeks).
  //    getDayCap + dayHoursByStaff are passed in by the grid, so no fetch is needed.
  useEffect(() => {
    if (!placeholder || !candidates || candidates.length === 0) return;
    if (!getDayCap || !dayHoursByStaff) return;
    const snapMonday = (iso) => {
      const m = new Date(String(iso).slice(0, 10) + 'T12:00:00');
      m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
      return m;
    };
    const startMon = snapMonday(placeholder.StartDate);
    const endMon = snapMonday(placeholder.EndDate || placeholder.StartDate);
    const spanned = Math.round((endMon - startMon) / (7 * 86400000)) + 1;
    const numWeeks = Math.min(Math.max(spanned, 5), 13);
    const weekMondays = [];
    for (let i = 0; i < numWeeks; i++) {
      const d = new Date(startMon); d.setDate(d.getDate() + i * 7);
      weekMondays.push(toISO(d));
    }
    const ids = candidates.map(c => c.StaffId);
    const rows = computeStaffWeeklyUtil({ staffIds: ids, weekMondays, getDayCap, dayHoursByStaff });
    const map = {};
    for (const r of rows) map[r.staffId] = r.weeks;
    setAvailability(map);
    setAvailMeta({ spanned });
  }, [placeholder, candidates, getDayCap, dayHoursByStaff]);

  // 3) When a candidate is picked, seed their preview from the optimizer.
  useEffect(() => {
    if (!pickedStaffId || !placeholder || !getDayCap || !entries || !dayHoursByStaff) return;
    if (previewByStaff[pickedStaffId]) return; // already seeded
    const dates = buildCandidateDates(placeholder.StartDate, placeholder.EndDate, pickedStaffId, getDayCap);
    const result = computeOptimizedHours({
      staffId: pickedStaffId,
      candidateDates: dates,
      totalHours,
      excludeEntryIds: new Set(),
      getDayCapFn: getDayCap,
      entriesArr: entries,
      dayHoursMap: dayHoursByStaff,
    });
    setPreviewByStaff(prev => ({
      ...prev,
      [pickedStaffId]: {
        proposedDailyHours: result.dailyHours,
        candidateDates: dates,
        overflowStart: false,
        overflowEnd: false,
      },
    }));
  }, [pickedStaffId, placeholder, getDayCap, entries, dayHoursByStaff, totalHours, previewByStaff]);

  if (!placeholder) return null;

  const handleExtendChange = ({ overflowStart, overflowEnd, dailyHours, expandedDates }) => {
    if (!pickedStaffId) return;
    setPreviewByStaff(prev => ({
      ...prev,
      [pickedStaffId]: {
        ...(prev[pickedStaffId] || {}),
        overflowStart,
        overflowEnd,
        candidateDates: expandedDates,
        proposedDailyHours: dailyHours,
      },
    }));
  };

  const handleDailyHoursChange = (next) => {
    if (!pickedStaffId) return;
    setPreviewByStaff(prev => ({
      ...prev,
      [pickedStaffId]: { ...(prev[pickedStaffId] || {}), proposedDailyHours: next },
    }));
  };

  const handleConfirm = async () => {
    if (!pickedStaffId) return;
    const pv = previewByStaff[pickedStaffId];
    if (!pv) return;
    const entriesPayload = Object.entries(pv.proposedDailyHours)
      .filter(([, h]) => Number(h) > 0.001)
      .map(([date, hours]) => ({ date, hours: Number(hours) }));
    if (entriesPayload.length === 0) {
      alert('No hours allocated. Adjust the distribution before confirming.');
      return;
    }
    setSaving(true);
    try {
      await assignPlaceholder({
        placeholderId: placeholder.PlaceholderId,
        staffId: pickedStaffId,
        createdBy: currentUser || null,
        entries: entriesPayload,
      });
      onAssigned?.();
    } catch (e) {
      alert('Failed to assign placeholder: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // Seed the form from the row. Dates are sliced rather than parsed — `new
  // Date('YYYY-MM-DD')` is UTC-parsed and would show the previous day in Central.
  function openEdit() {
    setEditDept(placeholder.DepartmentCode || '');
    setEditLevel(placeholder.Level || '');
    setEditHours(String(Number(placeholder.Hours) || ''));
    setEditStart(String(placeholder.StartDate).slice(0, 10));
    setEditEnd(String(placeholder.EndDate).slice(0, 10));
    setEditNotes(placeholder.Notes || '');
    setEditError(null);
    setEditing(true);
  }

  // The server may ABSORB this row into another one (real jobs only — see
  // findMergeablePlaceholder), in which case it returns the surviving id and this
  // placeholder is gone. Either way the grid has to refetch, so onEdited is called
  // with the result and the modal closes rather than showing a stale row.
  async function handleSaveEdit() {
    const hrs = parseFloat(editHours);
    if (!editDept || !editLevel) { setEditError('Department and level are required.'); return; }
    if (!Number.isFinite(hrs) || hrs <= 0) { setEditError('Hours must be greater than 0.'); return; }
    if (!editStart || !editEnd) { setEditError('Start and end dates are required.'); return; }
    if (editEnd < editStart) { setEditError('End date must be on or after the start date.'); return; }
    setSaving(true);
    setEditError(null);
    try {
      const res = await upsertPlaceholder({
        placeholderId: placeholder.PlaceholderId,
        // Kind is fixed for the life of a placeholder; send back whichever it holds.
        jobId: placeholder.JobId || undefined,
        customJobId: placeholder.CustomJobId || undefined,
        departmentCode: editDept,
        level: editLevel,
        hours: hrs,
        startDate: editStart,
        endDate: editEnd,
        notes: editNotes.trim() || null,
      });
      setEditing(false);
      onEdited?.(res);
    } catch (e) {
      setEditError(e.message || 'Could not save those changes.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deletePlaceholder(placeholder.PlaceholderId);
      onDeleted?.();
    } catch (e) {
      alert('Failed to delete placeholder: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sched-mgr-modal-backdrop" onClick={onClose}>
      <div className="sched-mgr-modal" style={{ width: 1080, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="sched-mgr-modal-header">
          <h3>Assign Placeholder</h3>
          <button className="sched-mgr-sidebar-close" onClick={onClose}>&times;</button>
        </div>

        {/* Placeholder summary. A custom job has no client, so its category stands in
            for the client line and the niche / on-site notes are suppressed — both are
            derived from the job's client. */}
        <div className="sched-mgr-modal-job">
          <span className="sched-mgr-modal-job-name">
            {isCustomJob ? (placeholder.CustomJobName || 'Custom job') : placeholder.JobName}
          </span>
          <span className="sched-mgr-modal-job-client">
            {isCustomJob ? (placeholder.CustomJobCategory || 'Internal') : placeholder.ClientName}
          </span>
          <span className="sched-mgr-modal-job-budget">
            {placeholder.Level} · {placeholder.DepartmentCode} · {totalHours.toFixed(2)}h
          </span>
          <span style={{ fontSize: 11, color: '#6B7280' }}>{dateLabel}</span>
          {placeholder.Notes && (
            <span style={{ fontSize: 11, color: '#6B7280', fontStyle: 'italic' }}>&ldquo;{placeholder.Notes}&rdquo;</span>
          )}
          {!isCustomJob && nicheMissing && <NicheGapFlag missing style={{ marginLeft: 8 }} />}
          {!isCustomJob && (
            <OnSiteFilterNote isOnSite={jobOnSite.isOnSite} officeCode={jobOnSite.officeCode}
              office={jobOnSite.office} style={{ marginLeft: 8 }} />
          )}
        </div>

        {/* Edit-in-place. Collapsed by default so the modal still opens on its main
            job (picking someone); expanding it is the deliberate act. */}
        {editing ? (
          <div style={{ margin: '0 4px 8px', padding: 10, border: '1px solid #93C5FD', borderRadius: 8, background: '#F8FAFC' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ flex: '1 1 130px', fontSize: 11, color: '#374151' }}>
                Department
                <select value={editDept} onChange={e => setEditDept(e.target.value)}
                  style={{ width: '100%', padding: 5, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }}>
                  {STAFF_DEPT_GROUPS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </label>
              <label style={{ flex: '1 1 130px', fontSize: 11, color: '#374151' }}>
                Level
                <select value={editLevel} onChange={e => setEditLevel(e.target.value)}
                  style={{ width: '100%', padding: 5, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }}>
                  {PLACEHOLDER_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>
              <label style={{ flex: '0 1 80px', fontSize: 11, color: '#374151' }}>
                Hours
                <input type="number" min="0" step="0.25" value={editHours}
                  onChange={e => setEditHours(e.target.value)}
                  style={{ width: '100%', padding: 5, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }} />
              </label>
              <label style={{ flex: '1 1 120px', fontSize: 11, color: '#374151' }}>
                Start
                <input type="date" value={editStart} onChange={e => setEditStart(e.target.value)}
                  style={{ width: '100%', padding: 5, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }} />
              </label>
              <label style={{ flex: '1 1 120px', fontSize: 11, color: '#374151' }}>
                End
                <input type="date" value={editEnd} onChange={e => setEditEnd(e.target.value)}
                  style={{ width: '100%', padding: 5, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }} />
              </label>
            </div>
            <label style={{ display: 'block', marginTop: 6, fontSize: 11, color: '#374151' }}>
              Label (optional)
              <input type="text" value={editNotes} onChange={e => setEditNotes(e.target.value)}
                placeholder="e.g. holding for Smith LLC, pending setup"
                style={{ width: '100%', padding: 5, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, boxSizing: 'border-box' }} />
            </label>
            {editError && <div style={{ marginTop: 6, fontSize: 11, color: '#991B1B' }}>{editError}</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
              <button className="sched-assign-btn" style={{ fontSize: 11, padding: '5px 12px' }}
                disabled={saving} onClick={handleSaveEdit}>Save changes</button>
              <button className="sched-mgr-pill" style={{ fontSize: 11 }}
                disabled={saving} onClick={() => { setEditing(false); setEditError(null); }}>Cancel</button>
              {!isCustomJob && (
                <span style={{ fontSize: 10, color: '#6B7280' }}>
                  Moving onto an existing placeholder for this job merges into it.
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ margin: '0 4px 8px' }}>
            <button style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 11, padding: 0 }}
              onClick={openEdit}>Edit placeholder (level, hours, dates)</button>
          </div>
        )}

        {/* Body — candidate list */}
        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, padding: '8px 4px' }}>
          {candidates === null && (
            <div style={{ padding: 16, fontSize: 12, color: '#9CA3AF' }}>Loading candidates…</div>
          )}
          {error && (
            <div style={{ padding: 12, background: '#FEF2F2', color: '#991B1B', borderRadius: 6, fontSize: 12, margin: 8 }}>
              Failed to load candidates: {error}
            </div>
          )}
          {candidates && candidates.length === 0 && !error && (
            <div style={{ padding: 16, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
              No staff at {placeholder.Level} · {placeholder.DepartmentCode}
              {jobOnSite.isOnSite && jobOnSite.officeCode ? ` in ${jobOnSite.officeCode}` : ''}.
              {jobOnSite.isOnSite && jobOnSite.officeCode && (
                <> This job is on-site, so only {jobOnSite.officeCode} staff are offered.</>
              )}
            </div>
          )}
          {/* Say why there are no fit scores, rather than leaving a list of
              score-less cards to be read as a glitch. */}
          {isCustomJob && candidates && candidates.length > 0 && (
            <div style={{ margin: '0 8px 8px', padding: '6px 9px', borderRadius: 6, background: '#F1F5F9', color: '#475569', fontSize: 11 }}>
              Internal work has no client or service line to match against, so these are
              everyone at {placeholder.Level} &middot; {placeholder.DepartmentCode}, listed
              alphabetically. Pick using the availability heatmap.
            </div>
          )}
          {candidates && candidates.length > 0 && (
            <div className="sched-sug-list">
              {candidates.map((c, idx) => {
                const isPicked = pickedStaffId === c.StaffId;
                const totalScore = Math.round(c.totalScore || 0);
                const pv = previewByStaff[c.StaffId];
                const proposedDailyHours = pv?.proposedDailyHours || {};
                const candidateDates = pv?.candidateDates || [];
                const overflowStart = pv?.overflowStart || false;
                const overflowEnd = pv?.overflowEnd || false;

                const allocSum = Object.values(proposedDailyHours).reduce((s, v) => s + (Number(v) || 0), 0);
                const allocSumR = Math.round(allocSum * 100) / 100;
                const unalloc = Math.round((totalHours - allocSum) * 100) / 100;

                // Over-cap warning for the Extend hint
                const hasOverCap = isPicked && candidateDates.some(iso => {
                  const cap = getDayCap ? getDayCap(c.StaffId, iso) : 8;
                  const existing = (dayHoursByStaff && dayHoursByStaff[c.StaffId]?.[iso]) || 0;
                  const proposed = proposedDailyHours[iso] || 0;
                  return existing + proposed > cap && cap > 0;
                });

                return (
                  <div key={c.StaffId}
                    className={`sched-sug-card${isPicked ? ' sched-sug-card--expanded sched-sug-card--selected' : ''}`}
                    onClick={() => setPickedStaffId(isPicked ? null : c.StaffId)}
                    style={isPicked
                      ? { flexDirection: 'column', alignItems: 'stretch', borderColor: '#6CC24A', boxShadow: '0 0 0 1px #6CC24A' }
                      : { flexDirection: 'column', alignItems: 'stretch' }}>

                    {/* Card header — same row as StaffSuggestions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                      <div className="sched-sug-rank">
                        {isCustomJob
                          ? `#${idx + 1}`
                          : (idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`)}
                      </div>
                      <div className="sched-sug-avatar"
                        style={{ background: isCustomJob ? BRAND.oceanBlue
                          : totalScore >= 60 ? BRAND.deepTeal : totalScore >= 30 ? BRAND.oceanBlue : '#9CA3AF' }}>
                        {(c.Initials || c.Name?.split(' ').map(w => w[0]).join('') || '??').toUpperCase().slice(0, 2)}
                      </div>
                      <div className="sched-sug-info">
                        <div className="sched-sug-name">{c.Name}</div>
                        <div className="sched-sug-level">{c.Level} &middot; {c.DepartmentCode || c.Department}</div>
                      </div>

                      {!isCustomJob && <MatchBadge tier={c.matchTier} />}
                      <GenTrackFlag band={bandByStaff[c.StaffId]} deptCode={c.DepartmentCode || c.Department} jobBucket={jobBucket} />
                      {!isCustomJob && c.interestScore > 0 && (
                        <span className="sched-sug-match" style={{ background: '#FEF3C7', color: '#92400E' }}>
                          &#9733; Interest Area
                        </span>
                      )}

                      {/* Score tier card + minibar. Both are omitted for a custom job:
                          the fit score is built from the client's niche and the job's
                          service line, and a custom job has neither. Rendering
                          `totalScore || 0` would print a confident 0 on every card,
                          which reads as "everyone is a terrible match" rather than
                          "there is nothing to score here". */}
                      {!isCustomJob && (<>
                        <div className="sched-sug-score" style={{
                          background: totalScore >= 60 ? '#F0FDF4' : totalScore >= 35 ? '#EFF6FF' : totalScore >= 15 ? '#FFFBEB' : '#F9FAFB',
                          borderColor: totalScore >= 60 ? '#BBF7D0' : totalScore >= 35 ? '#BFDBFE' : totalScore >= 15 ? '#FDE68A' : '#E5E7EB',
                        }}>
                          <div className="sched-sug-score-value" style={{
                            color: totalScore >= 60 ? BRAND.deepTeal : totalScore >= 35 ? BRAND.oceanBlue : totalScore >= 15 ? '#B45309' : '#9CA3AF',
                          }}>{totalScore}</div>
                          <div className="sched-sug-score-label">fit</div>
                        </div>

                        <div className="sched-sug-minibar">
                          <div className="sched-sug-minibar-fill" style={{
                            width: `${totalScore}%`,
                            background: totalScore >= 67 ? BRAND.brightGreen : totalScore >= 33 ? BRAND.sageGreen : '#F59E0B',
                          }} />
                        </div>
                      </>)}

                      {/* Availability heatmap — one cell per week in the placeholder
                          window, dated; bracket marks the actual assignment weeks. */}
                      {(() => {
                        const weeks = availability[c.StaffId];
                        if (!weeks || weeks.length === 0) return null;
                        const CELL_W = 16, CELL_GAP = 3;
                        const inRangeCount = availMeta.spanned != null ? Math.min(availMeta.spanned, weeks.length) : 0;
                        const bracketW = inRangeCount > 0 ? inRangeCount * CELL_W + (inRangeCount - 1) * CELL_GAP : 0;
                        return (
                          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0, marginTop: inRangeCount > 0 ? -9 : 0 }}>
                            <span style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Availability</span>
                            <div style={{ position: 'relative' }}>
                              <div style={{ display: 'flex', gap: CELL_GAP }}>
                                {weeks.map((w, wi) => {
                                  const free = 100 - (w.pct || 0);
                                  const bg = free >= 50 ? '#6CC24A' : free >= 20 ? '#F59E0B' : '#EF4444';
                                  const inRange = wi < inRangeCount;
                                  return (
                                    <div key={wi} title={`Wk of ${fmtMD(w.weekStart)}: ${free}% free (${Math.round((w.capacity || 0) - (w.scheduled || 0))}h of ${w.capacity || 0}h)`}
                                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                      <div style={{ width: CELL_W, height: 18, borderRadius: 3, background: bg, opacity: inRangeCount > 0 && !inRange ? 0.45 : 0.85 }} />
                                      <span style={{ fontSize: 7, lineHeight: 1, color: inRangeCount > 0 && !inRange ? '#C9CDD3' : '#9CA3AF' }}>{fmtMD(w.weekStart)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              {inRangeCount > 0 && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, width: bracketW, marginTop: 1, pointerEvents: 'none' }}>
                                  <div style={{ height: 3, borderLeft: `1px solid ${BRAND.deepTeal}`, borderRight: `1px solid ${BRAND.deepTeal}`, borderBottom: `1px solid ${BRAND.deepTeal}`, borderRadius: '0 0 2px 2px' }} />
                                  <div style={{ fontSize: 10, lineHeight: 1, marginTop: 1, fontWeight: 700, color: BRAND.deepTeal, textAlign: 'center', whiteSpace: 'nowrap' }}>Assignment</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Expand-on-pick preview panel */}
                    {isPicked && (
                      <div onClick={e => e.stopPropagation()}
                        style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: 10 }}>

                        {/* Extend toggles */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Distribution</span>
                          <ExtendToggles
                            staffId={c.StaffId}
                            candidateDates={buildCandidateDates(placeholder.StartDate, placeholder.EndDate, c.StaffId, getDayCap)}
                            totalHours={totalHours}
                            excludeEntryIds={new Set()}
                            getDayCap={getDayCap}
                            entries={entries}
                            dayHoursByStaff={dayHoursByStaff}
                            overflowStart={overflowStart}
                            overflowEnd={overflowEnd}
                            onChange={handleExtendChange}
                            hasOverCap={hasOverCap}
                          />
                        </div>

                        {/* Daily hours grid (collapsed by default) */}
                        <DailyHoursGrid
                          staffId={c.StaffId}
                          candidateDates={candidateDates}
                          dailyHours={proposedDailyHours}
                          origDailyHours={{}}
                          onChange={handleDailyHoursChange}
                          getDayCap={getDayCap}
                          entries={entries}
                          excludeEntryIds={new Set()}
                          collapsedDefault={true}
                          showTotal={true}
                        />

                        {/* Before/After view */}
                        <BeforeAfterSummary
                          originalDailyHours={{}}
                          proposedDailyHours={proposedDailyHours}
                          getDayCap={getDayCap}
                          ptoByStaffDay={ptoByStaffDay}
                          staffId={c.StaffId}
                          newStaff={c.Name}
                          newJob={placeholder.JobName}
                          isNewEntry={true}
                          dayHoursByStaff={dayHoursByStaff}
                          displayTotal={allocSumR}
                          holidayByDate={holidayByDate}
                          globalAssignmentByDate={globalAssignmentByDate}
                          staffDeptCode={c.DepartmentCode}
                        />

                        {/* Total chip + unallocated warning */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                          <span style={{ fontWeight: 700, color: '#003D4C' }}>
                            Σ {allocSumR.toFixed(2)} / {totalHours.toFixed(2)} h
                          </span>
                          {Math.abs(unalloc) > 0.01 && (
                            <span style={{ color: unalloc > 0 ? '#D97706' : '#DC2626', fontWeight: 600 }}>
                              {unalloc > 0
                                ? `${unalloc.toFixed(2)}h not allocated`
                                : `${Math.abs(unalloc).toFixed(2)}h over placeholder total`}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 4px', borderTop: '1px solid #F3F4F6', alignItems: 'center' }}>
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              style={{ background: 'none', border: 'none', color: '#991B1B', padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
              Delete placeholder
            </button>
          ) : (
            <>
              <span style={{ fontSize: 11, color: '#991B1B' }}>Delete this placeholder?</span>
              <button onClick={handleDelete} disabled={saving}
                style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                Yes, delete
              </button>
              <button onClick={() => setConfirmDelete(false)} disabled={saving}
                style={{ background: 'none', border: '1px solid #D1D5DB', color: '#374151', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} disabled={saving}
            style={{ background: 'none', border: '1px solid #D1D5DB', color: '#374151', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!pickedStaffId || saving}
            className="sched-assign-btn"
            style={{
              opacity: !pickedStaffId || saving ? 0.5 : 1,
              cursor: !pickedStaffId || saving ? 'not-allowed' : 'pointer',
            }}>
            {saving ? 'Assigning…' : 'Confirm assignment'}
          </button>
        </div>
      </div>
    </div>
  );
}
